import { useEffect, useRef, useState } from 'react';

import { QuestionSearchItem } from '@core/types/types.common';
import { DBQuestion } from '@core/types/types.db';
import { QuestionRelatedEndpoint } from '@core/types/types.endpoint.question';
import { Accordion, AccordionItem } from '@frontend/components/ui/accordion';
import { Separator } from '@frontend/components/ui/separator';
import { Question } from '@frontend/shared/components/Question/Question';
import { useBroadcaster } from '@frontend/shared/hooks/shared.hook.useBroadcaster';
import { callApi } from '@frontend/shared/util/util.callApi';

export interface QuestionRelatedProps {
  question: DBQuestion;
}

export function QuestionRelated({ question }: QuestionRelatedProps) {
  const [selectedId, setSelectedId] = useState<string>('');
  const [related, setRelated] = useState<QuestionSearchItem[]>([]);
  const fetchedCache = useRef<Record<string, boolean>>({});

  const fetchRelated = async (query: string) => {
    if (fetchedCache.current[query]) return;
    fetchedCache.current[query] = true;

    const [err, res] = await callApi<QuestionRelatedEndpoint>(
      '/question/related',
      {
        query,
      },
    );
    if (err || !res) return;
    setRelated((curr) => {
      const currIds = [question.id, ...curr.map((q) => q.id)];
      const filtered = res.filter((q) => !currIds.includes(q.id));
      if (!filtered.length) {
        return curr;
      }

      return [...curr, ...filtered.slice(0, 3)];
    });
  };

  useEffect(() => {
    fetchRelated(question.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for question updates
  useBroadcaster((e) => {
    if (e.event === 'question:delete') {
      setRelated(related.filter((q) => e.questionId !== q.id));
    }
    if (e.event === 'question:save') {
      setRelated(related.map((q) => (q.id === e.question.id ? e.question : q)));
    }
  });

  if (!related.length) return null;

  return (
    <>
      <Separator className="mt-20" />
      <h3 className="text-[13px] leading-4 tracking-tight text-[#033426cc] font-medium mt-3">
        Related questions
      </h3>
      <Accordion
        type="single"
        collapsible
        className="mb-4 mt-4"
        value={selectedId}
        onValueChange={(value) => {
          setSelectedId(value || '');
          const question = related.find((q) => q.id === value);
          if (question) {
            fetchRelated(question.title);
          }
        }}
      >
        {related?.map((q) => (
          <AccordionItem
            key={q.id}
            value={q.id}
            className="border-none mt-1.5"
            data-id={q.id}
          >
            <Question question={q} isExpanded={selectedId === q.id} />
          </AccordionItem>
        ))}
      </Accordion>
    </>
  );
}
